import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { theme } from '@Guidelines/Theme/theme';
import GeneratedDocumentRow from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/StageCard/GeneratedDocumentRow';
import resolveStageTone from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/StageCard/Helpers/resolveStageTone';
import type { StageTone } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/StageCard/Helpers/resolveStageTone';
import type WorkflowRunDocumentStageView from '@UI/Store/WorkflowRuns/Views/WorkflowRunDocumentStageView';
import { css } from '@emotion/react';
import { Check } from 'lucide-react';
import type * as React from 'react';

const BORDER_BY_TONE: Readonly<Record<StageTone, string>> = Object.freeze({
	active: theme.colors.border,
	waiting: theme.colors.border,
	running: theme.colors.border,
	verified: theme.colors.border,
	review: 'rgba(245, 166, 35, 0.6)',
	failed: 'rgba(232, 65, 122, 0.5)',
});

const NUMBER_FILL_BY_TONE: Readonly<Record<StageTone, string>> = Object.freeze({
	active: theme.colors.accent,
	running: theme.colors.accent,
	waiting: '#E3EAED',
	verified: theme.colors.validated,
	review: '#D98A0B',
	failed: theme.states.danger.solid,
});

const cardStyle = (tone: StageTone) => css`
	display: flex;
	align-items: flex-start;
	gap: 1rem;
	padding: 1.125rem;
	border-radius: 18px;
	background-color: ${tone === 'waiting' ? '#F7F9FA' : theme.colors.surface};
	border: 1.5px solid ${BORDER_BY_TONE[tone]};
`;

const numberStyle = (tone: StageTone) => css`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: 2.125rem;
	height: 2.125rem;
	border-radius: 50%;
	background-color: ${NUMBER_FILL_BY_TONE[tone]};
	color: ${tone === 'waiting' ? theme.colors.inkSecondary : theme.colors.surface};
	font-family: ${theme.fonts.body};
	font-size: 0.875rem;
	font-weight: ${theme.fontWeights.semibold};
`;

const bodyStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	flex: 1;
	min-width: 0;
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	margin: 0;
	font-size: 0.65625rem;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const documentListStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
	margin: 0;
	padding: 0;
	list-style: none;
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	readonly stage: WorkflowRunDocumentStageView;
	readonly elementCount: number;
	/** Whether the visitor may launch (« Réessayer »): permission, dossier not archived. */
	readonly canLaunch: boolean;
};

/** One stage of the generation: its number (colored by its state), its documents in parallel. */
const StageCard: React.FC<Props> = ({ subject, preparationId, stage, elementCount, canLaunch }) => {
	const tone = resolveStageTone(stage);
	const count = stage.documents.length;

	return (
		<div css={cardStyle(tone)}>
			<div css={numberStyle(tone)}>
				{tone === 'verified' ? <Check size={16} aria-hidden="true" /> : stage.stage + 1}
			</div>
			<div css={bodyStyle}>
				<p css={eyebrowStyle}>
					Étape {stage.stage + 1}
					{count > 1 ? ` · ${count} documents en parallèle` : ''}
				</p>
				<ul css={documentListStyle}>
					{stage.documents.map((document) => (
						<GeneratedDocumentRow
							key={document.stepId}
							subject={subject}
							preparationId={preparationId}
							stage={stage.stage}
							document={document}
							elementCount={elementCount}
							canLaunch={canLaunch}
						/>
					))}
				</ul>
			</div>
		</div>
	);
};

export default StageCard;
